import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import React, { useState} from "react";
import CustomTh from "../../../../components/table/CustomTh";
import CustomTd from "../../../../components/table/CustomTd";
import {DATE_TIME_FORMAT_NAME_MODEL, EMPTY_VALUES, getDateFormat} from "../../../../Apis/functions";
import CustomImageTable from "../../../../components/CustomImageTable";
import checkIcon from "../../../../assets/icon/checkIcon.svg";
import cancelIcon from "../../../../assets/icon/cancelIcon.svg";
import TableFunction from "../../../../components/table/TableFunction";
import {SearchByFilters, SimpleSearch} from "../../../../reduxToolkit/Search/SearchApi";
import {TABLES} from "../../../../Apis/Tables";
import {CRITERIA_MODEL, DEFAULT_LEFT, SEARCH_MODEL} from "../../../../components/table/Model/TableModel";
import {getAccessMethodIcon, getAccessMethodLabel} from "../../../../enums/accessMethodEnum";

/**
 * SectionAccessVehicleTabTableApp Component
 *
 * This component renders a table displaying vehicle access records,
 * including vehicle details, access attempts, and authentication methods.
 *
 * @Author: Ferro Zapata Arnulfo
 * @Email : azapata@ibl.mx
 * @JobTitle: Full Stack Developer
 *
 * @props {Object} data - The vehicle access data to be displayed in the table.
 * @props loaded
 * @props setSearchResults
 * @props setCriteria
 * @props setLoaded
 * @props setDeletedFlag
 * @props search
 * @props deletedFlag
 * @returns {Object} The table structure and pagination controls.
 */
export const SectionAccessVehicleTabTableApp = ({setBodyForExport, setCriteria, finalArray, modified, setModified}) => {

    const dispatch = useDispatch();
    const {t} = useTranslation();
    const tableId = "tbl_dpt_vehicless";
    const searchId = "txt_dpt_vehicless";
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const option = `${TABLES.VEHICLE_ACCESS_LOG}`;
    const leftPosition = DEFAULT_LEFT;

    // Function to generate the table header
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t('brand')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('sub_brand')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('color')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('plates')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('vin')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('accessDate')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('device')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('zone')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('successful_attempt')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('access_method')}</CustomTh>,
        ];
    }

    // Function to generate the table body
    const getTbody = () => {
        if (!results || results?.content?.length === 0)
            return {
                body: [],
                data: [],
            };


        let body = results?.content?.map((item, index) => {
            return (
                <>
                    <CustomTd tableId={tableId} tooltip={item?.name} fixed={true} left={leftPosition}
                              classes={"first-column"}>{item?.vehicle?.brand || item?.brand || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.vehicle?.subBrand || item?.subBrand || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.vehicle?.color || item?.color || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.vehicle?.plate || item?.plate || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.vehicle?.vin || item?.vin || EMPTY_VALUES.DEFAULT}</CustomTd>
                     <CustomTd tableId={tableId}>
                        {getDateFormat(item?.createdAt, null, DATE_TIME_FORMAT_NAME_MODEL.dateTimeWithSeconds) ||
                            EMPTY_VALUES.DEFAULT}
                    </CustomTd>
                    <CustomTd tableId={tableId}>{item?.device?.name || item?.deviceName || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.device?.zone?.name || item?.zoneName || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>
                        <span className="text-ellipsis">
                            <CustomImageTable image={item?.success ? checkIcon : cancelIcon}/>
                        </span>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <span className="text-ellipsis">
                           <CustomImageTable
                               isTooltip={true}
                               tooltip={getAccessMethodLabel(item?.accessMethod?.id || item?.accessMethodId,t)}
                               image={getAccessMethodIcon(item?.accessMethod?.id || item?.accessMethodId)}
                           />
                        </span>
                    </CustomTd>
                </>
            );
        });

        return {body, data: results?.content};
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({page, size: rowsPerPage, model:option});
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(
            ({payload: {data: { data, success }}}) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    }

    /**
     * Executes a paginated search request using advanced criteria.
     *
     * Builds the criteria list based on the advanced search (finalArray),
     * appends user criteria, and dispatches the search action through Redux.
     * Updates the results and resets the selected list once the data is returned.
     */
    const getDataAdvancedSearch = (page) => {
        const criteriaList = finalArray.map((item) => {
            return CRITERIA_MODEL.set(item);
        });
        const body = SEARCH_MODEL.setObject({page, size:rowsPerPage, model:option, criteria:criteriaList});
        setCriteria(body);
        dispatch(SearchByFilters({ option, body })).then(({ payload: {data: { data }}}) => {
            setResults(data);
        });
    }

    /**
     * Table functions and pagination controls.
     */
    const { thead, tbody, pagination, rowsPerPage} = TableFunction({
        data:results,
        getThead,
        getTbody,
        hasContainer: true,
        labelRowsPerPage: 'user_access_per_page',
        fixedPagination: false,

        getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch,//New - apply only with advanced search
        finalArray,//New - apply only with advanced search
    });

    return {
        tableId,
        searchId,
        thead,
        tbody,
        pagination,
    };
}
